import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { JSON_CONTENT_TYPE, OPPORTUNITY_SEARCH_TYPE } from "constants/app";
import { modalTypes } from "constants/types";
import { showErrorModal } from "slices/app/actions/error";
import { hideModal, showModal } from "slices/app/actions/modals";
import {
  addToJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import {
  getFormDataForNewOpportunity,
  getOpportunityUrl,
} from "slices/opportunity/opportunities/actions/helpers";
import { untrackOpportunity as untrackOpportunityFromSummary } from "slices/opportunity/summary/summarySlice";
import {
  fetchTrackedOpportunities,
  fetchTrackedOppsCount,
} from "slices/opportunity/tracked/actions";
import {
  markKanbanStale,
  removeById,
} from "slices/opportunity/tracked/trackedSlice";
import { store } from "slices/store";
import { getFullName } from "utils/getFullName";

import {
  INITIAL_SORTING,
  setCurrentFilter,
  setData,
  setIsLoading,
  setIsLoadingMore,
  setSavedSearches,
  setTabConfiguration,
  trackOpportunityByOppId,
} from "../opportunitiesSlice";
import {
  selectAdditionalFilters,
  selectOpportunitiesPageNumber,
} from "../selectors";
import fetchSearchTotals, { fetchUnfilteredTotal } from "./fetchSearchTotals";
import { getSourceConfig, PAGE_SIZE } from "./sourceConfig";

export { fetchSearchTotals, fetchUnfilteredTotal };

export const toEnumCase = (value = "") =>
  value
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .replace(/[-\s]+/g, "_")
    .toUpperCase();

const HEADERS = {
  headers: {
    "Content-Type": JSON_CONTENT_TYPE,
  },
};

const NEXTGEN_FILTER_PATH = "/nextgen/filter";

export const sanitizeFilters = (filters = {}) => {
  const cleaned = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === "" || value === null || value === undefined) continue;

    if (key === "active" && value === "all") continue;

    if (Array.isArray(value) && value.length === 0) continue;

    cleaned[key] = value;
  }

  if (cleaned.keywords) {
    cleaned.keywords = cleaned.keywords.trim();
  }

  return cleaned;
};

export const fetchFilteredOpportunities =
  ({
    sorting = INITIAL_SORTING,
    reset: resetPageNumber = false,
    type = OPPORTUNITY_SEARCH_TYPE.SAM_GOV,
    ...filters
  } = {}) =>
  async (dispatch) => {
    const state = store.getState();
    const statePageNumber = selectOpportunitiesPageNumber(state);
    const pageNumber = resetPageNumber ? 0 : statePageNumber || 0;
    const isLoadingMore = pageNumber > 0;
    const additionalFilters = selectAdditionalFilters(state);
    const allFilters = { ...additionalFilters, ...filters };
    const sanitized = sanitizeFilters(allFilters);
    const { searchUrl, buildPayload, annotateItem, mapFilters } =
      getSourceConfig(type);
    const cleanedFilters = mapFilters ? mapFilters(sanitized) : sanitized;

    if (isLoadingMore) {
      dispatch(setIsLoadingMore(true));
    } else {
      dispatch(addToJobQueue("fetchFilteredOpportunities"));
    }

    if (!isLoadingMore) {
      const hasFilters = Object.entries(cleanedFilters).length > 0;
      const totalResultsKnown =
        (state.opportunity?.opportunities?.totalResults || 0) > 0;
      if (hasFilters && !totalResultsKnown) {
        dispatch(fetchUnfilteredTotal(type));
      }
      dispatch(fetchSearchTotals(cleanedFilters, type));
    }

    const payload = buildPayload(cleanedFilters, pageNumber, sorting);

    try {
      const rsp = await localhost.post(searchUrl, payload, HEADERS);
      const rawData = rsp?.data || [];
      const data = rawData.map(annotateItem);
      const isLastPage = data.length < PAGE_SIZE;

      dispatch(
        setData({
          data,
          isLastPage,
          paginate: !resetPageNumber,
          pageNumber: pageNumber + 1,
        })
      );
    } catch (err) {
      console.error(err);
    }

    if (isLoadingMore) {
      dispatch(setIsLoadingMore(false));
    } else {
      dispatch(removeFromJobQueue("fetchFilteredOpportunities"));
    }
  };

const showTrackOpportunitySuccessBanner = () => (dispatch) => {
  dispatch(
    showGlobalAlert({
      variant: alertVariants.success,
      text: "Opportunity successfully tracked",
    })
  );
};

export const trackOpportunityWithSolNumber =
  (identifier, redirect = () => {}, type = OPPORTUNITY_SEARCH_TYPE.SAM_GOV) =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const { trackUrl, trackAction } = getSourceConfig(type);
    const url = trackUrl(identifier);
    try {
      const rsp = await localhost.get(url, undefined, HEADERS);
      dispatch(showTrackOpportunitySuccessBanner());
      const opportunityId = rsp?.data || [];
      // BE returns opportunityId on 200
      dispatch(trackAction(identifier, opportunityId));
      dispatch(markKanbanStale());
      redirect(opportunityId);
      return opportunityId;
    } catch (err) {
      dispatch(showErrorModal(err));
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const trackOpportunity =
  (opportunity, redirect = () => {}, type = OPPORTUNITY_SEARCH_TYPE.SAM_GOV) =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    const { trackUrl, trackAction, identifierKey } = getSourceConfig(type);
    const identifier = opportunity[identifierKey];
    const url = trackUrl(identifier);
    try {
      const rsp = await localhost.get(url, undefined, HEADERS);
      dispatch(showTrackOpportunitySuccessBanner());
      const opportunityId = rsp?.data || [];
      // BE returns opportunityId on 200
      dispatch(trackAction(identifier, opportunityId));
      dispatch(markKanbanStale());
      redirect(opportunityId);
      return opportunityId;
    } catch (err) {
      dispatch(showErrorModal(err));
      return null;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const trackOpportunityByOpportunityId =
  (opportunityId, redirect = () => {}) =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const url = `/nextgen/track/${opportunityId}`;
      await localhost.get(url, undefined, HEADERS);
      dispatch(showTrackOpportunitySuccessBanner());
      dispatch(trackOpportunityByOppId({ opportunityId }));
      dispatch(markKanbanStale());
      redirect();
      return true;
    } catch (err) {
      console.error(err);
      return false;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const untrackOpportunityById =
  (opportunityId, fromSummary = true, isManualOpp = false) =>
  async (dispatch) => {
    dispatch(addToJobQueue("untrackOpportunityById"));
    try {
      const url = `/nextgen/opportunity/${opportunityId}`;
      await localhost.delete(url, undefined, HEADERS);
      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: `Opportunity successfully ${isManualOpp ? "deleted" : "untracked"}`,
        })
      );
      if (fromSummary) {
        dispatch(untrackOpportunityFromSummary());
      } else {
        dispatch(fetchTrackedOppsCount());
      }
      dispatch(removeById(opportunityId));
    } catch (err) {
      console.error(err);
    } finally {
      dispatch(removeFromJobQueue("untrackOpportunityById"));
    }
  };

export const fetchOppAssociatedData = (opportunityId) => async (dispatch) => {
  dispatch(setIsLoading(true));
  try {
    const url = `/nextgen/opportunity/${opportunityId}/delete-check`;
    const rsp = await localhost.get(url, undefined, HEADERS);
    return rsp?.data || {};
  } catch (err) {
    dispatch(showErrorModal(err));
  } finally {
    dispatch(setIsLoading(false));
  }
};

export const startProposal =
  (opportunityId, copyAssignees = false) =>
  async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const url = `/nextgen/proposal/${opportunityId}`;
      const rsp = await localhost.post(url, undefined, {
        params: { copyAssignees },
      });

      return rsp?.data;
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(setIsLoading(false));
    }
  };

const confirmCopyAssignees = (dispatch, assigneeNames) =>
  new Promise((resolve) => {
    dispatch(
      showModal(modalTypes.CONFIRMATION, {
        headerText: "Auto-populate assignees?",
        bodyText: `This opp has assigned users (${assigneeNames}). Do you want to assign these users to the Proposal also?`,
        okButton: {
          text: "Yes",
          onOk: () => {
            dispatch(hideModal(modalTypes.CONFIRMATION));
            resolve(true);
          },
        },
        cancelButton: {
          text: "No",
          onCancel: () => resolve(false),
        },
      })
    );
  });

export const startProposalWithAssigneePrompt = (entity) => async (dispatch) => {
  const assignees = entity?.assignees ?? [];

  if (assignees.length === 0) {
    const proposalId = await dispatch(startProposal(entity.id));
    return proposalId;
  }

  const assigneeNames = assignees.map(getFullName).join(", ");
  const copyAssignees = await confirmCopyAssignees(dispatch, assigneeNames);

  const proposalId = await dispatch(startProposal(entity.id, copyAssignees));
  return proposalId;
};

export const getSearchesList =
  (skipLoadingState, category, type) => async (dispatch) => {
    if (!skipLoadingState) {
      dispatch(addToJobQueue("getSearchesList"));
    }
    try {
      const response = await localhost.get(NEXTGEN_FILTER_PATH, {
        params: { category, type },
      });
      dispatch(setSavedSearches(response.data));
    } catch (error) {
      dispatch(showErrorModal(error));
    } finally {
      if (!skipLoadingState) {
        dispatch(removeFromJobQueue("getSearchesList"));
      }
    }
  };

export const getTabConfiguration = (category) => async (dispatch) => {
  dispatch(addToJobQueue("getTabConfiguration"));
  try {
    const response = await localhost.get(NEXTGEN_FILTER_PATH, {
      params: {
        category,
      },
    });

    const tabConfiguration =
      response.data?.find(
        (filter) => filter.name === "Opportunity Search Integrations Tabs"
      ) || null;

    dispatch(setTabConfiguration(tabConfiguration));

    return tabConfiguration;
  } catch (error) {
    dispatch(showErrorModal(error));
    dispatch(setTabConfiguration(null));

    return null;
  } finally {
    dispatch(removeFromJobQueue("getTabConfiguration"));
  }
};

export const getSearch = (id, type) => async (dispatch, getState) => {
  dispatch(addToJobQueue("getSearchDetails"));
  try {
    const response = await localhost.get(`/nextgen/filter/${id}`);
    const filters = response.data.value;
    dispatch(setCurrentFilter(filters));
    dispatch(
      fetchFilteredOpportunities({
        reset: true,
        sorting: getState()?.opportunity?.opportunities?.sorting,
        ...filters,
        type,
      })
    );
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("getSearchDetails"));
  }
};

export const saveSearch =
  (name, search, category, type) => async (dispatch) => {
    dispatch(addToJobQueue("saveSearch"));
    try {
      const response = await localhost.post(NEXTGEN_FILTER_PATH, {
        category,
        type: type !== undefined ? toEnumCase(type) : undefined,
        name,
        value: search,
      });

      dispatch(getSearchesList(undefined, category, type));
      return response.data;
    } catch (error) {
      dispatch(showErrorModal(error, "Save operation failed"));
    } finally {
      dispatch(removeFromJobQueue("saveSearch"));
    }
  };

export const updateSearch =
  (id, name, updatedSearch, category, type) => async (dispatch) => {
    dispatch(addToJobQueue("updateSearch"));
    try {
      await localhost.put(`/nextgen/filter/${id}`, {
        category,
        type: type !== undefined ? toEnumCase(type) : undefined,
        name,
        value: updatedSearch,
      });
      dispatch(getSearchesList(undefined, category, type));
      return true;
    } catch (error) {
      dispatch(showErrorModal(error, "Update operation failed"));
      return false;
    } finally {
      dispatch(removeFromJobQueue("updateSearch"));
    }
  };

export const deleteSearch = (id, category, type) => async (dispatch) => {
  dispatch(addToJobQueue("deleteSearch"));
  try {
    await localhost.delete(`/nextgen/filter/${id}`);
    dispatch(getSearchesList(undefined, category, type));
  } catch (error) {
    dispatch(showErrorModal(error));
  } finally {
    dispatch(removeFromJobQueue("deleteSearch"));
  }
};

export const createNewOpportunity = (data) => async (dispatch, getState) => {
  dispatch(addToJobQueue("createNewOpportunity"));

  const oppType = getState().opportunity.summary.oppType;
  const url = getOpportunityUrl(oppType);
  const formData = getFormDataForNewOpportunity(data, oppType);

  try {
    const rsp = await localhost.post(url, formData, HEADERS);
    dispatch(markKanbanStale());
    dispatch(fetchTrackedOpportunities());
    dispatch(fetchTrackedOppsCount());
    return rsp?.data;
  } catch (error) {
    dispatch(showErrorModal(error));
    return false;
  } finally {
    dispatch(removeFromJobQueue("createNewOpportunity"));
  }
};

export const createOpportunityAndStartProposal =
  (data, navigate) => async (dispatch, getState) => {
    dispatch(addToJobQueue("createOpportunityAndStartProposal"));

    const oppType = getState().opportunity.summary.oppType;
    const url = getOpportunityUrl(oppType);
    const formData = getFormDataForNewOpportunity(data, oppType);

    try {
      const { data: oppResponse } = await localhost.post(
        url,
        formData,
        HEADERS
      );
      const { data: proposalId } = await localhost.post(
        `/nextgen/proposal/${oppResponse.id}`
      );
      navigate(`/proposals/view/${proposalId}`);
    } catch (err) {
      dispatch(showErrorModal(err));
    } finally {
      dispatch(removeFromJobQueue("createOpportunityAndStartProposal"));
    }
  };

export const getCurrentOpportunityDownloads =
  (opportunityId, handleSuccess) => async (dispatch) => {
    await localhost
      .get(`/document/queue/status/opportunity/${opportunityId}`)
      .then(async (response) => {
        const { data } = response;
        handleSuccess(data);
      })
      .catch((error) => {
        dispatch(showErrorModal(error));
      });
  };
